import { Link, useSearchParams } from 'react-router-dom'
import { useAppState } from '../state/appState'
import { prefetchVods, useChannelsMeta } from '../hooks/useKick'
import { DEFAULT_SLUG } from '../lib/storage'
import { Avatar } from './Avatar'

type Props = {
  onAddClick: () => void
  showFavoritesOnlyToggle: boolean
}

export function TabStrip({ onAddClick, showFavoritesOnlyToggle }: Props) {
  const { channels, ui, removeTab, toggleFavorite, updateUi } = useAppState()
  const [params, setParams] = useSearchParams()
  const active = params.get('tab') ?? channels[0]?.slug ?? DEFAULT_SLUG

  const meta = useChannelsMeta(channels.map((c) => c.slug))
  const visible = ui.favoritesOnly ? channels.filter((c) => c.favorite) : channels

  const select = (slug: string) => {
    const next = new URLSearchParams(params)
    next.set('tab', slug)
    setParams(next, { replace: true })
  }

  return (
    <div className="tabstrip" role="tablist" aria-label="Channels">
      {visible.map((ref) => {
        const channel = meta.get(ref.slug)
        const isActive = ref.slug === active
        return (
          <div
            key={ref.slug}
            role="tab"
            aria-selected={isActive}
            className={`tab ${isActive ? 'is-active' : ''}`}
          >
            <Link
              to={`/?tab=${encodeURIComponent(ref.slug)}`}
              onClick={() => select(ref.slug)}
              onMouseEnter={() => prefetchVods(ref.slug)}
              onFocus={() => prefetchVods(ref.slug)}
              style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}
            >
              <Avatar src={channel?.profilePic} name={channel?.username ?? ref.slug} size={20} />
              <span className="tab-label">{channel?.username ?? ref.slug}</span>
              {channel?.isLive && <span className="live-dot" title="Live now" />}
            </Link>

            <span className="tab-actions">
              <button
                type="button"
                className={`icon-btn ${ref.favorite ? 'is-starred' : ''}`}
                title={ref.favorite ? 'Unfavorite channel' : 'Favorite channel'}
                aria-label={ref.favorite ? 'Unfavorite channel' : 'Favorite channel'}
                disabled={ref.builtIn}
                onClick={() => toggleFavorite(ref.slug)}
              >
                {ref.favorite ? '★' : '☆'}
              </button>

              {!ref.builtIn && (
                <button
                  type="button"
                  className="icon-btn"
                  title="Close tab"
                  aria-label={`Close ${ref.slug} tab`}
                  onClick={() => {
                    if (ref.slug === active) {
                      const nextVisible = visible.filter((c) => c.slug !== ref.slug)
                      select(nextVisible[0]?.slug ?? DEFAULT_SLUG)
                    }
                    removeTab(ref.slug)
                  }}
                >
                  ✕
                </button>
              )}
            </span>
          </div>
        )
      })}

      <button type="button" className="tab-add" onClick={onAddClick} title="Add channel" aria-label="Add channel">
        +
      </button>

      {showFavoritesOnlyToggle && channels.length > 4 && (
        <button
          type="button"
          className="btn btn-ghost"
          style={{ alignSelf: 'center', marginLeft: 8, marginBottom: 4, fontSize: 12 }}
          onClick={() => updateUi({ favoritesOnly: !ui.favoritesOnly })}
          title={ui.favoritesOnly ? 'Show all tabs' : 'Show favorites only'}
        >
          {ui.favoritesOnly ? '★ Favorites' : '☆ All'}
        </button>
      )}
    </div>
  )
}